import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [org, setOrg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setOrg(data.organization || null);
    } catch {
      setUser(false);
      setOrg(null);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    load();
  }, [load]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data);
    setOrg(data.organization || null);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    setOrg(data.organization || null);
    return data;
  };

  const googleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
    setUser(data);
    setOrg(data.organization || null);
    return data;
  };

  const guestLogin = async () => {
    const { data } = await api.post("/auth/guest");
    setUser(data);
    setOrg(data.organization || null);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setOrg(null);
  };

  const refreshOrg = useCallback(async () => {
    try {
      const { data } = await api.get("/me/org");
      setOrg(data);
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, org, setOrg, login, register, googleSession, guestLogin, logout, refreshOrg, reload: load }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
